/**
 * Enhanced Onboarding — 3-Step Role-Based Flow
 * Step 1: Role selection → sets workspace + org type
 * Step 2: Intent/use-case selection → personalises the experience
 * Step 3: Quick setup (org creation) + guided first action → navigate to first task
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import {
  Building2, Layers, BarChart3, TrendingUp, GraduationCap,
  ClipboardList, Package, Zap, Shield, Target, Activity, FileText,
  TrendingDown, FileCheck, BookOpen, Award, Users, Globe, BarChart2,
  Check, ArrowRight, ChevronLeft, Sparkles
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Role configuration ─────────────────────────────────────────────────────
const ROLES = [
  {
    id: "sme",
    label: "Small & Medium Enterprise",
    short: "SME",
    icon: Building2,
    color: "#059669",
    bg: "bg-emerald-50",
    border: "border-emerald-500",
    desc: "Recyclers, manufacturers, SMEs — measure your impact, access green finance",
    orgType: "sme",
  },
  {
    id: "project",
    label: "Project Developer",
    short: "Project",
    icon: Layers,
    color: "#3B82F6",
    bg: "bg-blue-50",
    border: "border-blue-500",
    desc: "Climate projects, infrastructure, renewables — structure opportunities and attract capital",
    orgType: "project",
  },
  {
    id: "corporate",
    label: "Corporate / Multinational",
    short: "Corporate",
    icon: BarChart3,
    color: "#6366F1",
    bg: "bg-indigo-50",
    border: "border-indigo-500",
    desc: "Large organizations with multiple sites — track emissions, set targets, manage disclosures",
    orgType: "corporate",
  },
  {
    id: "fi",
    label: "Financial Institution",
    short: "FI",
    icon: TrendingUp,
    color: "#F59E0B",
    bg: "bg-amber-50",
    border: "border-amber-500",
    desc: "Banks, DFIs, impact funds — screen investments, manage climate risk, review deals",
    orgType: "financial",
  },
  {
    id: "training",
    label: "Climate Learning",
    short: "Training",
    icon: GraduationCap,
    color: "#10B981",
    bg: "bg-teal-50",
    border: "border-teal-500",
    desc: "Practitioners, analysts, students — understand GHG, get certified, apply knowledge",
    orgType: "sme",
  },
];

// ── Intent options per role ────────────────────────────────────────────────
const INTENTS = {
  sme: [
    { id: "measure_emissions", label: "Measure my emissions", desc: "Create a credible GHG inventory for my business", icon: ClipboardList },
    { id: "get_financing",     label: "Get investment-ready", desc: "Build an investment pack and access green finance", icon: Package      },
    { id: "reduce_costs",      label: "Reduce energy costs", desc: "Cut diesel and electricity bills with solar and efficiency", icon: Zap       },
    { id: "compliance",        label: "Meet compliance requirements", desc: "Prepare for ESG or regulatory climate disclosure", icon: Shield   },
  ],
  project: [
    { id: "develop_project",  label: "Develop a climate project", desc: "Structure and document a new project opportunity", icon: Layers     },
    { id: "calculate_impact", label: "Calculate project impact", desc: "Quantify emission reductions and co-benefits", icon: BarChart2      },
    { id: "find_financing",   label: "Find project financing", desc: "Build an investment case for capital providers", icon: TrendingUp     },
    { id: "carbon_credits",   label: "Generate carbon credits", desc: "Pursue creditable emission reduction units", icon: Globe            },
  ],
  corporate: [
    { id: "track_emissions",      label: "Track emissions across sites", desc: "Multi-site GHG inventory and management reporting", icon: Activity     },
    { id: "set_targets",          label: "Set and manage targets", desc: "Net Zero, SBTi, and interim pathway planning", icon: Target              },
    { id: "disclosure",           label: "Prepare ESG disclosure", desc: "TCFD, GRI, or regulatory compliance reporting", icon: FileText            },
    { id: "identify_reductions",  label: "Find reduction opportunities", desc: "Identify highest-impact decarbonization actions", icon: TrendingDown },
  ],
  fi: [
    { id: "screen_deals",       label: "Screen investment opportunities", desc: "Evaluate climate-aligned deals in your pipeline", icon: TrendingUp    },
    { id: "manage_risk",        label: "Manage climate portfolio risk", desc: "Analyse exposure to transition and physical risk", icon: Shield        },
    { id: "review_deals",       label: "Review submitted packs", desc: "Evaluate formal financing requests from SMEs", icon: FileCheck             },
    { id: "portfolio_analysis", label: "Analyse portfolio alignment", desc: "Paris-aligned portfolio assessment and reporting", icon: BarChart3      },
  ],
  training: [
    { id: "understand_ghg",   label: "Understand GHG Protocol basics", desc: "Learn the foundation of climate accounting", icon: BookOpen          },
    { id: "get_certified",    label: "Get professionally certified", desc: "Earn recognised credentials in climate science", icon: Award            },
    { id: "apply_knowledge",  label: "Apply to my current role", desc: "Use climate intelligence tools in your work", icon: Activity              },
    { id: "team_capability",  label: "Build team capability", desc: "Upskill your team on climate and sustainability", icon: Users               },
  ],
};

// ── Guided first actions (role + intent → destination + label) ────────────
const GUIDED_ACTIONS = {
  sme: {
    measure_emissions:{ path:"/assessments",            label:"Create My First Assessment", detail:"Set up your emissions baseline — the foundation of everything." },
    get_financing:    { path:"/assessments",            label:"Build My Investment Pack",    detail:"Turn your data into a finance-ready opportunity for green finance." },
    reduce_costs:     { path:"/calculator",             label:"Run Solar ROI Analysis",      detail:"Calculate how much you can save on diesel and grid energy today." },
    compliance:       { path:"/assessments",            label:"Start Climate Assessment",    detail:"Build the GHG inventory your disclosure reporting requires." },
  },
  project: {
    develop_project:  { path:"/assessments",            label:"Create My Project Record",    detail:"Document your project and establish the climate baseline." },
    calculate_impact: { path:"/assets",                 label:"Set Up Project Inputs",       detail:"Define your project scope and collect activity data." },
    find_financing:   { path:"/assessments",            label:"Build Investment Case",       detail:"Structure your impact data for investor presentation." },
    carbon_credits:   { path:"/assessments",            label:"Start Impact Measurement",    detail:"Begin creditable emission reduction quantification." },
  },
  corporate: {
    track_emissions:      { path:"/corporate/structure",label:"Set Up My Organization",      detail:"Map sites, business units, and emission sources across your portfolio." },
    set_targets:          { path:"/corporate",          label:"View Emissions Dashboard",    detail:"See your current emissions landscape and set reduction targets." },
    disclosure:           { path:"/assessments",        label:"Start Climate Assessment",    detail:"Build the GHG inventory your disclosure framework requires." },
    identify_reductions:  { path:"/corporate/initiatives",label:"View Decarbonization Plan",detail:"Explore where you can cut emissions most efficiently." },
  },
  fi: {
    screen_deals:       { path:"/deals",                label:"Open Deal Pipeline",          detail:"See all pre-screened climate investment opportunities ready for review." },
    manage_risk:        { path:"/fi",                   label:"Open Portfolio Dashboard",    detail:"Analyse climate risk and alignment across your investment portfolio." },
    review_deals:       { path:"/fi/financing-reviews", label:"Review Submitted Packs",      detail:"Evaluate investment packs submitted for financing consideration." },
    portfolio_analysis: { path:"/fi/projects",          label:"View FI Projects",            detail:"Deep-dive into individual project assessments and data." },
  },
  training: {
    understand_ghg:   { path:"/academy",                label:"Start Learning GHG Basics",   detail:"Begin your GHG Protocol learning journey with structured modules." },
    get_certified:    { path:"/academy",                label:"View Certification Courses",  detail:"Enroll in accredited climate accounting and reporting courses." },
    apply_knowledge:  { path:"/assessments",            label:"Run a Practice Assessment",   detail:"Apply your climate knowledge with a real GHG inventory exercise." },
    team_capability:  { path:"/academy",                label:"Explore the Academy",         detail:"Find courses for your entire team — from fundamentals to advanced." },
  },
};

const SECTORS = [
  "Agriculture", "Construction", "Energy", "Financial Services", "Healthcare",
  "Manufacturing", "Mining", "Real Estate", "Retail & FMCG", "Technology",
  "Transport & Logistics", "Waste Management & Recycling", "Water & Utilities", "Other"
];
const COUNTRIES = [
  "South Africa", "Kenya", "Nigeria", "Ghana", "Ethiopia", "Tanzania",
  "Uganda", "Rwanda", "Mozambique", "Zimbabwe", "Zambia", "Botswana",
  "Senegal", "Ivory Coast", "Morocco", "Egypt", "Other"
];

// ── Progress indicator ─────────────────────────────────────────────────────
function ProgressDots({ step }) {
  return (
    <div className="flex items-center gap-2" data-testid="progress-dots">
      {[0,1,2].map(i => (
        <div key={i} className={`rounded-full transition-all duration-300 ${
          i < step ? "w-6 h-2 bg-[#059669]" :
          i === step ? "w-8 h-2 bg-[#0F172A]" : "w-2 h-2 bg-slate-300"
        }`} />
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function Onboarding() {
  const { setActiveRole, switchOrg } = useAuth();
  const navigate = useNavigate();
  const [step,     setStep]    = useState(0);
  const [loading,  setLoading] = useState(false);
  const [selected, setSelected]= useState({ role: null, intent: null });
  const [org, setOrg] = useState({ name: "", sector: "", country: "" });

  const roleConf   = ROLES.find(r => r.id === selected.role);
  const intents    = INTENTS[selected.role] || [];
  const guidedAction = selected.role && selected.intent
    ? GUIDED_ACTIONS[selected.role]?.[selected.intent]
    : null;

  // ── Step 1: Role Selection ───────────────────────────────────────────────
  const handleRoleSelect = async (roleId) => {
    setSelected(s => ({ ...s, role: roleId }));
    setActiveRole(roleId);
    setStep(1);
  };

  // ── Step 3: Create org + navigate to guided action ───────────────────────
  const handleComplete = async () => {
    if (!org.name.trim()) { toast.error("Please enter your organization name."); return; }
    setLoading(true);
    try {
      const orgType = ROLES.find(r => r.id === selected.role)?.orgType || "sme";
      const res = await client.post("/organizations", {
        name: org.name, sector: org.sector, country: org.country, type: orgType,
      });
      switchOrg(res.data);
      toast.success("Welcome to DeCarbonIQ!");
      navigate(guidedAction?.path || "/dashboard");
    } catch {
      // Org creation failure — still proceed (org may already exist)
      navigate(guidedAction?.path || "/dashboard");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col" data-testid="onboarding-page">
      {/* Top bar */}
      <div className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-8 flex-shrink-0">
        <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" />
        <ProgressDots step={step} />
        <div className="w-24 text-right">
          <p className="text-xs text-slate-400">Step {step + 1} of 3</p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl">

          {/* ── STEP 1: Role Selection ── */}
          {step === 0 && (
            <div data-testid="step-role-selection">
              <div className="text-center mb-10">
                <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Welcome to DeCarbonIQ</p>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-3">
                  What describes your work?
                </h1>
                <p className="text-slate-500 text-base">Choose your workspace. You can switch anytime.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {ROLES.map(({ id, label, short, icon: Icon, color, bg, border, desc }) => (
                  <button key={id} onClick={() => handleRoleSelect(id)}
                    data-testid={`role-card-${id}`}
                    className={`group relative text-left p-5 bg-white border-2 border-slate-200 rounded-xl hover:border-2 hover:shadow-lg transition-all duration-200`}
                    style={{ '--hover-border': color }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = color}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "#E2E8F0"}
                  >
                    <div className={`w-11 h-11 ${bg} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon size={22} style={{ color }} />
                    </div>
                    <p className="text-sm font-semibold text-slate-900 mb-1">{label}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                    <div className="absolute top-4 right-4 w-5 h-5 rounded-full border-2 border-slate-200 group-hover:border-current group-hover:bg-current transition-all flex items-center justify-center"
                      style={{ '--tw-border-opacity': 1 }}>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP 2: Intent Selection ── */}
          {step === 1 && roleConf && (
            <div data-testid="step-intent-selection">
              <button onClick={() => setStep(0)} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-sm mb-8 transition-colors">
                <ChevronLeft size={16} /> Back
              </button>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${roleConf.color}18` }}>
                  <roleConf.icon size={20} style={{ color: roleConf.color }} />
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">{roleConf.short} workspace</p>
                  <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] tracking-tight">
                    What are you trying to achieve?
                  </h1>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {intents.map(({ id, label, desc, icon: Icon }) => {
                  const isSelected = selected.intent === id;
                  return (
                    <button key={id}
                      onClick={() => { setSelected(s => ({ ...s, intent: id })); setStep(2); }}
                      data-testid={`intent-card-${id}`}
                      className={`group text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? "shadow-lg"
                          : "bg-white border-slate-200 hover:shadow-md"
                      }`}
                      style={isSelected ? { borderColor: roleConf.color, backgroundColor: `${roleConf.color}08` } : {}}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = roleConf.color; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = "#E2E8F0"; }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: `${roleConf.color}15` }}>
                          <Icon size={18} style={{ color: roleConf.color }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 mb-0.5">{label}</p>
                          <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── STEP 3: Quick Setup + Guided Action ── */}
          {step === 2 && roleConf && guidedAction && (
            <div data-testid="step-guided-action">
              <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 text-sm mb-8 transition-colors">
                <ChevronLeft size={16} /> Back
              </button>
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Org setup form */}
                <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-[#0F172A] mb-1">Almost ready</h2>
                  <p className="text-sm text-slate-500 mb-5">Set up your organization to get started.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Organization Name *</label>
                      <input type="text" value={org.name} onChange={e => setOrg(o => ({...o, name: e.target.value}))}
                        placeholder={roleConf.id === "fi" ? "AfriBank Capital" : roleConf.id === "corporate" ? "Patel Group Holdings" : "Green Cycle Recyclers Ltd"}
                        className="w-full h-11 px-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': roleConf.color }}
                        data-testid="org-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Sector</label>
                        <select value={org.sector} onChange={e => setOrg(o => ({...o, sector: e.target.value}))}
                          className="w-full h-11 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2"
                          data-testid="org-sector-select">
                          <option value="">Select sector</option>
                          {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Country</label>
                        <select value={org.country} onChange={e => setOrg(o => ({...o, country: e.target.value}))}
                          className="w-full h-11 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2"
                          data-testid="org-country-select">
                          <option value="">Select country</option>
                          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <button onClick={handleComplete} disabled={!org.name.trim() || loading}
                    data-testid="complete-setup-btn"
                    className="mt-6 w-full h-12 rounded-xl text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                    style={{ backgroundColor: roleConf.color }}>
                    {loading
                      ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <><span>{guidedAction.label}</span><ArrowRight size={15} /></>}
                  </button>
                  <button onClick={() => navigate("/dashboard")}
                    className="mt-3 w-full text-xs text-slate-400 hover:text-slate-600 py-2">
                    Skip and go to dashboard instead
                  </button>
                </div>

                {/* Guided action preview */}
                <div className="lg:col-span-2">
                  <div className="bg-[#0F172A] rounded-xl p-6 text-white h-full flex flex-col">
                    <div className="w-10 h-10 rounded-xl mb-4 flex items-center justify-center"
                      style={{ backgroundColor: `${roleConf.color}30` }}>
                      <Sparkles size={18} style={{ color: roleConf.color }} />
                    </div>
                    <p className="text-xs text-slate-400 uppercase tracking-widest mb-1.5">Your first step</p>
                    <h3 className="text-base font-bold mb-2" style={{ color: roleConf.color }}>
                      {guidedAction.label}
                    </h3>
                    <p className="text-sm text-slate-300 leading-relaxed flex-1">{guidedAction.detail}</p>
                    <div className="mt-5 pt-4 border-t border-slate-800">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: roleConf.color }} />
                        <span className="capitalize">{roleConf.short} workspace</span>
                        <span>·</span>
                        <span>Role confirmed</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
